import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useNavigate } from 'react-router';
import {
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import Button from '@mui/material/Button';

import Api from '@barboxx/api';
import { Category } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

interface Props {
    category: Category;
}

export const CategoryDeleteCard = ({ category }: Props) => {
    const notifications = useNotifications();
    const navigate = useNavigate();
    const [open, dialogActions] = useBool();

    const mutateDeleteCategory = useMutation({
        mutationKey: ['deleteCategory', category.id],
        mutationFn: () =>
            Api.Categories.Category.deleteCategory({ id: category.id }),
    });

    const onDelete = async () => {
        mutateDeleteCategory
            .mutateAsync()
            .then(({ data }) => {
                notifications.show('Deleted category!', {
                    severity: 'success',
                });
                navigate(-1);
            })
            .catch(e => {
                notifications.show('Error deleting category!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardHeader title="Delete Category" />
            <CardContent>
                <Stack gap={2} direction="row">
                    <Typography flex={1}>
                        Are you sure you want to delete this category and all
                        associated menu-items with it?
                    </Typography>

                    <Button
                        onClick={dialogActions.on}
                        color="error"
                        variant="outlined"
                    >
                        Delete
                    </Button>
                </Stack>
            </CardContent>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Delete Category
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this category?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={dialogActions.off} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        onClick={onDelete}
                        color="error"
                        variant="contained"
                        loading={mutateDeleteCategory.isPending}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
